import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Filter, Search } from "lucide-react";
import { listSecurities } from "@/lib/api";
import type { SecuritySummary, ConfidenceBand } from "@/lib/api/types";
import { fmtScore } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Label, Select, TextInput } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/Table";
import {
  ConfidenceBadge,
  ThesisBadge,
} from "@/components/ui/Badges";
import { LoadingState } from "@/components/ui/States";

type SortKey = "ticker" | "score" | "confidence" | "thesis";

// G1 — asset-class filter chips. Values match SecurityType enum on the
// backend; the empty string means "no filter".
const ASSET_CLASS_CHIPS: { value: string; label: string }[] = [
  { value: "", label: "All" },
  { value: "equity", label: "Equity" },
  { value: "etf", label: "ETF" },
  { value: "crypto", label: "Crypto" },
  { value: "fx", label: "FX" },
  { value: "commodity", label: "Commodity" },
];

export function ScreenerPage() {
  const securities = useQuery({
    queryKey: ["screener", "securities"],
    // No weightVectorId on purpose: the API then attaches each security's
    // latest score from the default vector for its *own* asset class, so
    // equity / ETF / crypto rows all show real scores instead of "—".
    // limit covers the full ~1,250-name universe (was 1000, which truncated it).
    queryFn: () => listSecurities({ limit: 2000 }),
  });

  const [search, setSearch] = useState("");
  const [exchange, setExchange] = useState<string>("");
  const [confidence, setConfidence] = useState<string>("");
  const [minScore, setMinScore] = useState<string>("");
  const [assetClass, setAssetClass] = useState<string>("");
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortAsc, setSortAsc] = useState(false);

  const exchanges = useMemo(() => {
    const set = new Set<string>();
    (securities.data ?? []).forEach((s) => set.add(s.exchange_mic));
    return Array.from(set).sort();
  }, [securities.data]);

  const filtered = useMemo(() => {
    const rows = securities.data ?? [];
    const min = minScore === "" ? null : Number(minScore);
    const lower = search.trim().toLowerCase();
    return rows
      .filter((r) => (lower ? r.ticker.toLowerCase().includes(lower) || r.name.toLowerCase().includes(lower) : true))
      .filter((r) => (exchange ? r.exchange_mic === exchange : true))
      .filter((r) => (assetClass ? r.security_type === assetClass : true))
      .filter((r) => (confidence ? r.latest_confidence === (confidence as ConfidenceBand) : true))
      .filter((r) => (min !== null ? (r.latest_score ?? -Infinity) >= min : true))
      .sort((a, b) => sortRows(a, b, sortKey, sortAsc));
  }, [securities.data, search, exchange, assetClass, confidence, minScore, sortKey, sortAsc]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Screener</h1>
        <p className="text-2xs text-foreground-muted">
          {filtered.length} of {securities.data?.length ?? 0} securities
        </p>
      </header>

      <Panel
        title={
          <span className="flex items-center gap-2">
            <Filter size={14} className="text-foreground-muted" />
            Filters
          </span>
        }
      >
        {/* G1 — asset-class chip row.  Mutually exclusive; "All" clears. */}
        <div className="mb-3 flex flex-wrap gap-1.5">
          {ASSET_CLASS_CHIPS.map((chip) => {
            const active = assetClass === chip.value;
            return (
              <button
                key={chip.value || "all"}
                type="button"
                onClick={() => setAssetClass(chip.value)}
                className={`rounded-full border px-2.5 py-0.5 text-2xs font-medium transition ${
                  active
                    ? "border-accent bg-accent-subtle text-accent-strong"
                    : "border-border bg-surface-base text-foreground-secondary hover:bg-surface-subtle"
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="space-y-1">
            <Label>Search</Label>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <TextInput
                placeholder="Ticker or name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-7"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Exchange</Label>
            <Select value={exchange} onChange={(e) => setExchange(e.target.value)}>
              <option value="">All</option>
              {exchanges.map((mic) => (
                <option key={mic} value={mic}>
                  {mic}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Confidence</Label>
            <Select value={confidence} onChange={(e) => setConfidence(e.target.value)}>
              <option value="">Any</option>
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
              <option value="insufficient">insufficient</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Minimum composite</Label>
            <TextInput
              type="number"
              min={0}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Sort by</Label>
            <div className="flex gap-1">
              <Select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
                <option value="score">Composite</option>
                <option value="ticker">Ticker</option>
                <option value="confidence">Confidence</option>
                <option value="thesis">Thesis</option>
              </Select>
              <button
                type="button"
                className="rounded-md border border-border bg-surface-base px-2 text-xs text-foreground-secondary hover:bg-surface-subtle"
                onClick={() => setSortAsc((v) => !v)}
                title={sortAsc ? "Ascending" : "Descending"}
              >
                {sortAsc ? "↑" : "↓"}
              </button>
            </div>
          </div>
        </div>
      </Panel>

      <Panel bodyClassName="px-0 py-0">
        {securities.isLoading && <LoadingState message="Loading universe…" />}
        {securities.data && (
          <DataTable
            dense
            rows={filtered}
            rowKey={(r) => r.id}
            columns={[
              {
                key: "ticker",
                header: "Ticker",
                cell: (r) => (
                  <Link
                    to={`/company/${r.id}`}
                    className="font-medium text-foreground-primary hover:text-accent-strong"
                  >
                    {r.ticker}
                  </Link>
                ),
              },
              {
                key: "name",
                header: "Name",
                cell: (r) => <span className="text-foreground-secondary">{r.name}</span>,
              },
              {
                key: "exchange",
                header: "MIC",
                cell: (r) => (
                  <span className="font-mono text-2xs text-foreground-muted">{r.exchange_mic}</span>
                ),
              },
              {
                key: "score",
                header: "Composite",
                cell: (r) => <span className="tabular-nums">{fmtScore(r.latest_score)}</span>,
                align: "right",
              },
              {
                key: "confidence",
                header: "Confidence",
                cell: (r) =>
                  r.latest_confidence ? <ConfidenceBadge band={r.latest_confidence} /> : "—",
              },
              {
                key: "thesis",
                header: "Thesis",
                cell: (r) =>
                  r.latest_thesis ? <ThesisBadge status={r.latest_thesis} /> : "—",
              },
            ]}
            empty="No securities match these filters."
          />
        )}
      </Panel>
    </div>
  );
}

function sortRows(
  a: SecuritySummary,
  b: SecuritySummary,
  key: SortKey,
  asc: boolean,
): number {
  const dir = asc ? 1 : -1;
  if (key === "ticker") return a.ticker.localeCompare(b.ticker) * dir;
  if (key === "score") {
    const av = a.latest_score ?? -Infinity;
    const bv = b.latest_score ?? -Infinity;
    return (av - bv) * dir;
  }
  if (key === "confidence") {
    return (a.latest_confidence ?? "").localeCompare(b.latest_confidence ?? "") * dir;
  }
  return (a.latest_thesis ?? "").localeCompare(b.latest_thesis ?? "") * dir;
}
